/* ────────────────────────────────────────────────────────────
   LABO · Bioactive Silica — Interactive Shader Background
   Content-bound version (v2): renders inside .content-wrapper
   so header and footer stay untouched.

   Markup required (inside your page template):

     <div class="content-wrapper page-silica-section" id="page-...">
       <canvas id="silica-bg-canvas" aria-hidden="true"></canvas>
       {{ page.content }}
     </div>

   If the <canvas> is missing, the script will auto-insert one as
   the first child of the first .content-wrapper.page-silica-section
   it finds.
   ──────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var VERT = '#version 300 es\n' +
    'in vec2 a_pos;\n' +
    'void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }\n';

  var FRAG = '#version 300 es\n' +
    'precision highp float;\n' +
    'out vec4 outColor;\n' +
    'uniform vec2  u_resolution;\n' +
    'uniform float u_time;\n' +
    'uniform vec2  u_mouse;\n' +
    'uniform vec3  u_click;\n' +
    'uniform vec3  u_click2;\n' +
    'uniform vec3  u_click3;\n' +
    'uniform float u_hover;\n' +

    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }\n' +
    'float noise(vec2 p){\n' +
    '  vec2 i = floor(p), f = fract(p);\n' +
    '  vec2 u = f*f*(3.0-2.0*f);\n' +
    '  return mix(mix(hash(i), hash(i+vec2(1,0)), u.x),\n' +
    '             mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);\n' +
    '}\n' +
    'float fbm(vec2 p){\n' +
    '  float v=0.0, a=0.5;\n' +
    '  for(int i=0;i<5;i++){ v+=a*noise(p); p=p*2.03 + vec2(3.1,1.7); a*=0.5; }\n' +
    '  return v;\n' +
    '}\n' +
    'vec3 hexGrid(vec2 p){\n' +
    '  vec2 s = vec2(1.0, 1.7320508);\n' +
    '  vec4 hC = floor(vec4(p, p - vec2(0.5, 1.0)) / s.xyxy) + 0.5;\n' +
    '  vec4 h = vec4(p - hC.xy*s, p - (hC.zw + 0.5)*s);\n' +
    '  bool pickA = dot(h.xy,h.xy) < dot(h.zw,h.zw);\n' +
    '  vec2 best = pickA ? h.xy : h.zw;\n' +
    '  vec2 id   = pickA ? hC.xy : (hC.zw + 0.5);\n' +
    '  vec2 ap = abs(best);\n' +
    '  float d = 0.5 - max(ap.x * 0.8660254 + ap.y*0.5, ap.y);\n' +
    '  return vec3(d, id);\n' +
    '}\n' +

    'void main(){\n' +
    '  vec2 uv = gl_FragCoord.xy / u_resolution.xy;\n' +
    '  vec2 p  = uv; p.x *= u_resolution.x/u_resolution.y;\n' +
    '  vec2 mp = u_mouse; mp.x *= u_resolution.x/u_resolution.y;\n' +

    '  float t = u_time*0.035;\n' +
    '  vec2 drift = vec2(t, t*0.6);\n' +

    '  vec2 toM = mp - p;\n' +
    '  float md = length(toM);\n' +
    '  float lens = exp(-md*md*2.2) * (0.55 + 0.45*u_hover);\n' +
    '  vec2 warp = toM * lens * 0.06;\n' +
    '  vec2 q = p*5.0 + drift + warp*3.0;\n' +

    '  float shimmer = 0.0;\n' +
    '  for (int k=0;k<3;k++){\n' +
    '    vec3 c = k==0?u_click: (k==1?u_click2:u_click3);\n' +
    '    if (c.z >= 0.0 && c.z < 3.5){\n' +
    '      vec2 cp = c.xy; cp.x *= u_resolution.x/u_resolution.y;\n' +
    '      float d = length(p-cp);\n' +
    '      float w = exp(-pow((d - c.z*0.45)*5.0, 2.0)) * exp(-c.z*0.7);\n' +
    '      shimmer += w;\n' +
    '    }\n' +
    '  }\n' +

    '  vec3 hex = hexGrid(q);\n' +
    '  vec3 hex2 = hexGrid(q*0.42 + 13.0);\n' +
    '  float facet  = smoothstep(0.02, 0.18, hex.x);\n' +
    '  float facet2 = smoothstep(0.04, 0.22, hex2.x);\n' +

    '  float cellSeed  = hash(hex.yz);\n' +
    '  float cellSeed2 = hash(hex2.yz);\n' +
    '  float breathe  = 0.5 + 0.5*sin(u_time*0.35 + cellSeed*6.2831);\n' +
    '  float breathe2 = 0.5 + 0.5*sin(u_time*0.22 + cellSeed2*6.2831 + 1.4);\n' +

    '  float caustic = fbm(p*3.4 + drift*4.0);\n' +
    '  caustic += fbm(p*7.0 - drift*2.0 + warp*8.0) * 0.5;\n' +
    '  caustic = caustic / 1.5;\n' +
    '  float cursorCaustic = exp(-md*2.2) * (0.5 + 0.5*u_hover);\n' +

    // Premium light-blue palette
    '  vec3 bone      = vec3(0.915, 0.950, 0.985);\n' +
    '  vec3 champagne = vec3(0.70,  0.85,  1.00);\n' +
    '  vec3 mineral   = vec3(0.40,  0.65,  0.88);\n' +
    '  vec3 deepBone  = vec3(0.82,  0.88,  0.95);\n' +

    '  float vign = smoothstep(1.3, 0.2, length(uv - vec2(0.5,0.55)));\n' +
    '  vec3 col = mix(deepBone, bone, vign);\n' +

    '  float edge  = 1.0 - facet;\n' +
    '  float edge2 = 1.0 - facet2;\n' +
    '  col = mix(col, col * 0.94, edge2 * 0.55);\n' +
    '  col += champagne * edge2 * 0.06 * (0.5 + 0.5*breathe2);\n' +
    '  col = mix(col, col * 0.96, edge * 0.35);\n' +
    '  col += champagne * edge * 0.14 * (0.4 + 0.6*breathe);\n' +

    '  col += vec3(0.14, 0.11, 0.07) * caustic * 0.35;\n' +
    '  col += mineral * 0.12 * cursorCaustic;\n' +
    '  col += champagne * 0.22 * cursorCaustic * u_hover;\n' +

    '  col += champagne * lens * edge * 0.35;\n' +
    '  col += vec3(1.0,0.98,0.92) * lens * facet * 0.06;\n' +

    '  col += mix(vec3(1.0, 0.95, 0.85), mineral, 0.15) * shimmer * 0.35;\n' +
    '  col += champagne * shimmer * edge * 0.8;\n' +

    '  col += (hash(gl_FragCoord.xy + u_time) - 0.5) * 0.010;\n' +
    '  col *= mix(0.82, 1.02, vign);\n' +

    '  outColor = vec4(col, 1.0);\n' +
    '}\n';

  function init() {
    // Auto-create the canvas at the TOP of <body> if missing.
    var canvas = document.getElementById('silica-bg-canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'silica-bg-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      document.body.insertBefore(canvas, document.body.firstChild);
    }

    var gl = canvas.getContext('webgl2', { antialias: true, alpha: false, premultipliedAlpha: false });
    if (!gl) return;  // CSS gradient fallback stays visible

    function compile(type, src) {
      var sh = gl.createShader(type);
      gl.shaderSource(sh, src); gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('[silica-bg]', gl.getShaderInfoLog(sh));
      }
      return sh;
    }
    var vs = compile(gl.VERTEX_SHADER, VERT);
    var fs = compile(gl.FRAGMENT_SHADER, FRAG);
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[silica-bg]', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var U = {
      resolution: gl.getUniformLocation(prog, 'u_resolution'),
      time:       gl.getUniformLocation(prog, 'u_time'),
      mouse:      gl.getUniformLocation(prog, 'u_mouse'),
      click:      gl.getUniformLocation(prog, 'u_click'),
      click2:     gl.getUniformLocation(prog, 'u_click2'),
      click3:     gl.getUniformLocation(prog, 'u_click3'),
      hover:      gl.getUniformLocation(prog, 'u_hover'),
    };

    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timeScale = reducedMotion ? 0.2 : 1.0;

    var state = {
      mouse: [0.5, 0.5], target: [0.5, 0.5], hover: 1, targetHover: 1,
      clicks: [[0,0,-1],[0,0,-1],[0,0,-1]],
      start: performance.now(),
    };

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var cssW = Math.max(1, window.innerWidth  || document.documentElement.clientWidth  || 1);
      var cssH = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
      var w = Math.max(2, Math.round(cssW * dpr));
      var h = Math.max(2, Math.round(cssH * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('load', resize);
    requestAnimationFrame(resize);

    // Mouse tracking — viewport-relative, always on.
    window.addEventListener('pointermove', function (e) {
      state.target = [e.clientX / window.innerWidth, 1 - e.clientY / window.innerHeight];
      state.targetHover = 1;
    }, { passive: true });
    window.addEventListener('pointerdown', function (e) {
      var x = e.clientX / window.innerWidth;
      var y = 1 - e.clientY / window.innerHeight;
      state.clicks[2] = state.clicks[1];
      state.clicks[1] = state.clicks[0];
      state.clicks[0] = [x, y, 0];
    }, { passive: true });

    var prev = performance.now();
    function frame(now) {
      var dt = Math.min(0.05, (now - prev) / 1000); prev = now;
      resize();
      var k = 1 - Math.pow(0.001, dt);
      state.mouse[0] += (state.target[0] - state.mouse[0]) * k;
      state.mouse[1] += (state.target[1] - state.mouse[1]) * k;
      state.hover   += (state.targetHover - state.hover) * k;
      for (var i = 0; i < 3; i++) if (state.clicks[i][2] >= 0) state.clicks[i][2] += dt;

      var t = ((now - state.start) / 1000) * timeScale;
      gl.uniform2f(U.resolution, canvas.width, canvas.height);
      gl.uniform1f(U.time, t);
      gl.uniform2f(U.mouse, state.mouse[0], state.mouse[1]);
      gl.uniform3f(U.click,  state.clicks[0][0], state.clicks[0][1], state.clicks[0][2]);
      gl.uniform3f(U.click2, state.clicks[1][0], state.clicks[1][1], state.clicks[1][2]);
      gl.uniform3f(U.click3, state.clicks[2][0], state.clicks[2][1], state.clicks[2][2]);
      gl.uniform1f(U.hover, state.hover);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
